import { CheckOutlined, CopyOutlined, StarOutlined } from '@ant-design/icons';
import { AuthSubscriptions } from '@hefe/auth';
import { t } from '@hefe/i18n';
import { PracticeTimesSchedule } from '@hefe/models';
import {
  BasketballPracticeTimesApi,
  currentUserSelector,
  ModalsSlice,
} from '@hefe/store';
import { clubHasSubscription, formatDate } from '@hefe/utils';
import { Button, Popconfirm, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import AddEditPracticeTimesScheduleForm from '../../forms/add-edit-practice-times-schedule/add-edit-practice-times-schedule.form';

interface PracticeTimesSchedulesComponentProps {
  schedules: PracticeTimesSchedule[];
  loading?: boolean;
}

export default function PracticeTimesSchedulesComponent(
  props: PracticeTimesSchedulesComponentProps
) {
  const { schedules } = props;

  const dispatch = useAppDispatch();

  const user = useSelector(currentUserSelector);

  const [deleteSchedule, { isLoading: isDeletingSchedule }] =
    BasketballPracticeTimesApi.useDeletePracticeTimesScheduleMutation();
  const [markAsDefault, { isLoading: isMarkingAsDefault }] =
    BasketballPracticeTimesApi.useMarkPracticeTimesScheduleAsDefaultMutation();

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const addFormTooltipText = useMemo(() => {
    const hasSubscription = clubHasSubscription(AuthSubscriptions.second, user);
    if (props.loading) {
      t('loading.title');
    } else if (schedules.length > 0 && !hasSubscription) {
      return t('errors.permissions.practice_time_schedules');
    }

    return undefined;
  }, [props.loading, schedules.length, user]);

  const onClickMarkAsDefault = () => {
    const schedule = schedules.find((s) => s.id === selectedRowKeys[0]);
    if (schedule) {
      markAsDefault(schedule);
    }
  };

  const onClickDuplicateSchedule = () => {
    const schedule = schedules.find((s) => s.id === selectedRowKeys[0]);
    if (!schedule) {
      return;
    }

    dispatch(
      ModalsSlice.modalsActions.openModal({
        formId: `add-edit-practice-times-schedule-form-${schedule.id}`,
        component: (
          <AddEditPracticeTimesScheduleForm
            schedule={schedule}
            duplicate={true}
          />
        ),
        title: t('practices.administration.practice_times.schedules.duplicate'),
      })
    );
  };

  const onClickEditSchedule = (schedule: PracticeTimesSchedule) => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        formId: `add-edit-practice-times-schedule-form-${schedule.id}`,
        component: <AddEditPracticeTimesScheduleForm schedule={schedule} />,
        title: t('practices.administration.practice_times.schedules.edit'),
      })
    );
  };

  return (
    <CustomTable
      rowKey="id"
      name="practice-times-schedules"
      dataSource={schedules}
      onRowSelect={(selectedRowKeys) => setSelectedRowKeys(selectedRowKeys)}
      selectedRowKeys={selectedRowKeys}
      columns={[
        {
          key: 'name',
          title: 'Name',
          dataIndex: 'name',
          default: true,
        },
        {
          key: 'from',
          title: t('from'),
          dataIndex: 'from',
          default: true,
          render: (from: string) => (from ? formatDate(from) : '---'),
        },
        {
          key: 'till',
          title: t('till'),
          dataIndex: 'till',
          default: true,
          render: (till: string) => (till ? formatDate(till) : '---'),
        },
        {
          key: 'active',
          title: t('active'),
          dataIndex: 'active',
          default: true,
          render: (active: boolean) => (active ? <CheckOutlined /> : null),
        },
        {
          key: 'default',
          title: t('default'),
          dataIndex: 'default',
          default: true,
          render: (defaultValue: boolean) =>
            defaultValue ? <CheckOutlined /> : null,
        },
      ]}
      addForm={{
        formId: 'add-edit-practice-times-schedule-form-new',
        component: <AddEditPracticeTimesScheduleForm />,
        title: t('practices.administration.practice_times.schedules.add'),
        disabledTooltip: addFormTooltipText,
      }}
      deleteAction={{
        action: (ids) => {
          const id = ids[0];
          if (!id) {
            return;
          }

          const schedule = schedules.find((schedule) => schedule.id === id);
          if (schedule) {
            deleteSchedule(schedule)
              .unwrap()
              .then(() => setSelectedRowKeys([]));
          }
        },
        loading: isDeletingSchedule,
        multiDelete: false,
      }}
      onRowClick={(schedule: PracticeTimesSchedule) =>
        onClickEditSchedule(schedule)
      }
      toolbarItems={[
        <Button.Group>
          <Tooltip title={t('duplicate')}>
            <Button
              disabled={selectedRowKeys.length !== 1}
              icon={<CopyOutlined />}
              onClick={() => onClickDuplicateSchedule()}
            />
          </Tooltip>
          <Tooltip
            title={t(
              'practices.administration.practice_times.schedules.mark_as_default.title'
            )}
          >
            <Popconfirm
              title={t(
                'practices.administration.practice_times.schedules.mark_as_default.title'
              )}
              description={t(
                'practices.administration.practice_times.schedules.mark_as_default.description'
              )}
              disabled={selectedRowKeys.length !== 1}
              onConfirm={() => onClickMarkAsDefault()}
            >
              <Button
                disabled={selectedRowKeys.length !== 1}
                loading={isMarkingAsDefault}
                icon={<StarOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Button.Group>,
      ]}
    />
  );
}
